import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Check, Mail, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "../../lib/api";
import { ENQUIRY_ID, TERMS_PATH, departureStatus, formatDepartureRange } from "../../data/holidays";
import { LocalizedLink } from "../../lib/i18nRouting";
import { useDepartures } from "../../lib/useDepartures";
import { holidaysLang } from "../../i18n";
import { Chapter, Reveal, useHolidayScroll } from "./shared";
import ShareTrip from "./ShareTrip";
import { useEarlyBird } from "../../lib/useEarlyBird";

const PKG_INDEX = { p3: 0, p5: 1, p7: 2, custom: 3 };
const FLEX = "flexible";

const initial = {
  full_name: "", email: "", country: "", riders: 2, non_riders: 0,
  package: "", month: "", experience: "", transfer: "", message: "", is_trade: false, departure: FLEX,
};

export default function HolidayEnquiryForm({ preset }) {
  const { t, i18n } = useTranslation();
  const opts = t("holidays.form.options", { returnObjects: true });
  const DEPARTURES_2027 = useDepartures();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const scrollTo = useHolidayScroll();
  const eb = useEarlyBird();
  useEffect(() => { if (done) scrollTo(ENQUIRY_ID); }, [done, scrollTo]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Preselect package / trade flag / departure when arriving from a CTA.
  useEffect(() => {
    if (!preset) return;
    if (preset === "trade") set("is_trade", true);
    else if (preset === "early-bird") setForm((f) => ({ ...f, message: f.message || t("holidays.form.early_bird_message") }));
    else if (preset.startsWith("dep:")) {
      const id = preset.slice(4);
      const d = DEPARTURES_2027.find((x) => x.id === id);
      if (d) {
        set("departure", d.id);
        set("package", opts.package[PKG_INDEX[d.pkg]]);
        const m = new Date(d.start + "T00:00:00").toLocaleString("en-GB", { month: "long" });
        const monthOpt = opts.month.find((o) => o.startsWith(m));
        if (monthOpt) set("month", monthOpt);
      }
    }
    else if (PKG_INDEX[preset] !== undefined) set("package", opts.package[PKG_INDEX[preset]]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.country || !form.package || !form.month || !form.experience || !form.transfer) {
      return toast.error(t("holidays.form.toast_required"));
    }
    setSubmitting(true);
    try {
      const res = await api.post("/holiday-enquiries", {
        ...form,
        departure: form.departure === FLEX ? t("holidays.form.departure_flexible") : formatDepartureRange(DEPARTURES_2027.find((d) => d.id === form.departure), "en"),
        riders: Number(form.riders),
        non_riders: Number(form.non_riders),
        lang: i18n.language,
      });
      if (res?.data?.id) {
        setDone(true);
        toast.success(t("holidays.form.toast_success"));
      }
    } catch (err) {
      const status = err?.response?.status;
      toast.error((status >= 400 && status < 500 && formatApiErrorDetail(err.response?.data?.detail)) || t("holidays.form.toast_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id={ENQUIRY_ID} className="bg-bone py-24 lg:py-40 scroll-mt-20" data-testid="holidays-enquiry">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-4">
          <Chapter number={t("holidays.form.chapter")} label={t("holidays.form.eyebrow")} />
          <Reveal delay={0.1}>
            <h2 className="font-display text-5xl sm:text-6xl text-forest leading-[1.02] mt-8">
              {t("holidays.form.title_a")}<br /><span className="italic">{t("holidays.form.title_b")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2} className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed">{t("holidays.form.intro")}</Reveal>
          <Reveal delay={0.3} className="mt-10 bg-forest text-bone p-6">
            <div className="text-xs uppercase tracking-wider-3 opacity-70 mb-4">Apuseni Active</div>
            <a href="tel:+40752354222" className="flex items-center gap-3 link-underline" data-testid="enquiry-phone"><Phone className="w-4 h-4" /> +40 752 354 222</a>
            <a href="mailto:contact@apuseniactive.ro" className="flex items-center gap-3 mt-3 link-underline" data-testid="enquiry-email"><Mail className="w-4 h-4" /> contact@apuseniactive.ro</a>
          </Reveal>
        </div>

        <div className="lg:col-span-8">
          {done ? (
            <Success onReset={() => { setForm(initial); setDone(false); }} />
          ) : (
            <form onSubmit={submit} className="bg-white border border-bone-deep p-6 sm:p-10 lg:p-12 space-y-8" data-testid="holiday-enquiry-form" noValidate>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label={t("holidays.form.fields.name")}>
                  <input type="text" required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="enq-name" className="hol-input" autoComplete="name" />
                </Field>
                <Field label={t("holidays.form.fields.email")}>
                  <input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="enq-email" className="hol-input" autoComplete="email" />
                </Field>
                <Field label={t("holidays.form.fields.country")}>
                  <input type="text" required value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="enq-country" className="hol-input" autoComplete="country-name" />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label={t("holidays.form.fields.riders")}>
                    <input type="number" min={1} max={50} required value={form.riders} onChange={(e) => set("riders", e.target.value)} data-testid="enq-riders" className="hol-input" />
                  </Field>
                  <Field label={t("holidays.form.fields.non_riders")}>
                    <input type="number" min={0} max={50} required value={form.non_riders} onChange={(e) => set("non_riders", e.target.value)} data-testid="enq-non-riders" className="hol-input" />
                  </Field>
                </div>
                <Select label={t("holidays.form.fields.package")} value={form.package} onChange={(v) => set("package", v)} options={opts.package} testid="enq-package" placeholder={t("holidays.form.select_placeholder")} />
                <Select label={t("holidays.form.fields.month")} value={form.month} onChange={(v) => set("month", v)} options={opts.month} testid="enq-month" placeholder={t("holidays.form.select_placeholder")} />
                <Field label={t("holidays.form.fields.departure")}>
                  <select value={form.departure} onChange={(e) => set("departure", e.target.value)} data-testid="enq-departure" className="hol-input">
                    <option value={FLEX}>{t("holidays.form.departure_flexible")}</option>
                    {DEPARTURES_2027.map((d) => {
                      const full = departureStatus(d) === "full";
                      return (
                        <option key={d.id} value={d.id} disabled={full}>
                          {formatDepartureRange(d, holidaysLang(i18n.language))} · {t("holidays.departures.package")}{full ? ` — ${t("holidays.form.departure_full")}` : ""}
                        </option>
                      );
                    })}
                  </select>
                </Field>
                <Select label={t("holidays.form.fields.experience")} value={form.experience} onChange={(v) => set("experience", v)} options={opts.experience} testid="enq-experience" placeholder={t("holidays.form.select_placeholder")} />
                <Select label={t("holidays.form.fields.transfer")} value={form.transfer} onChange={(v) => set("transfer", v)} options={opts.transfer} testid="enq-transfer" placeholder={t("holidays.form.select_placeholder")} />
              </div>

              <Field label={t("holidays.form.fields.message")}>
                <textarea rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder={t("holidays.form.message_placeholder")} data-testid="enq-message" className="hol-input resize-none" />
              </Field>

              <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="enq-trade-label">
                <input type="checkbox" checked={form.is_trade} onChange={(e) => set("is_trade", e.target.checked)} data-testid="enq-trade" className="mt-1 w-4 h-4 accent-[#1f3a2e]" />
                <span className="text-sm text-forest leading-snug">{t("holidays.form.fields.trade")}</span>
              </label>

              <div className="flex flex-col sm:flex-row sm:items-center gap-5 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="enq-submit"
                  className="btn-lift w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-forest text-bone px-10 py-5 min-h-[56px] text-xs uppercase tracking-wider-3 disabled:opacity-60 hover:bg-forest-deep"
                >
                  {submitting ? t("holidays.form.submitting") : t("holidays.form.submit")} <ArrowUpRight className="w-4 h-4" />
                </button>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {t("holidays.form.privacy")}{" "}
                  {t("holidays.terms.form_link")}{" "}
                  <LocalizedLink to={TERMS_PATH} data-testid="form-terms-link" className="text-forest link-underline">{t("holidays.terms.link_label")}</LocalizedLink>.
                </p>
              </div>
              {eb.active && <p className="text-xs text-terracotta leading-relaxed border-t border-bone-deep pt-5" data-testid="form-early-bird-note">{t("holidays.form.early_bird_note")}</p>}
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Success({ onReset }) {
  const { t } = useTranslation();
  return (
    <div className="bg-forest text-bone p-10 lg:p-16 h-full flex flex-col justify-center" data-testid="enquiry-success">
      <div className="w-14 h-14 bg-bone text-forest flex items-center justify-center"><Check className="w-7 h-7" /></div>
      <h3 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight mt-8">
        {t("holidays.form.success_title_a")}<br /><span className="italic">{t("holidays.form.success_title_b")}</span>
      </h3>
      <p className="mt-6 text-base md:text-lg text-bone/80 leading-relaxed max-w-xl">{t("holidays.form.success_body")}</p>
      <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10">
        <button type="button" onClick={onReset} data-testid="enq-reset" className="self-start text-xs uppercase tracking-wider-3 link-underline">
          {t("holidays.form.success_another")}
        </button>
        <ShareTrip />
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider-3 text-moss block mb-2">{label}</span>
      {children}
    </label>
  );
}

function Select({ label, value, onChange, options, testid, placeholder }) {
  return (
    <Field label={label}>
      <select required value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="hol-input">
        <option value="" disabled>{placeholder}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </Field>
  );
}
